import type {
  PasskeyAuthenticationCredentialJSON,
  PublicKeyCredentialDescriptorJSON,
} from '@clovyr/pollen/user';
import { base64URLToArrayBuffer, bytesToBase64URL } from '@clovyr/pollen/util/base64';

import { config as appConfig } from '@/init/config';

export const LOCAL_SSO_PROVIDER = 'webauthn_clovyr';

export interface LocalSsoRequest {
  clientID: string;
  audience: string;
  redirectURI: string;
  challenge: string;
  state: string;
  returnTo?: string;
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
}

interface LocalSsoCallbackPayload {
  version: 1;
  provider: typeof LOCAL_SSO_PROVIDER;
  request: LocalSsoRequest;
  assertion: PasskeyAuthenticationCredentialJSON;
}

function requiredParam(params: URLSearchParams, name: string): string {
  const value = params.get(name)?.trim();
  if (!value) {
    throw new Error(`missing ${name}`);
  }
  return value;
}

function isSecureOrigin(url: URL): boolean {
  return (
    url.protocol === 'https:' ||
    (url.protocol === 'http:' &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]'))
  );
}

function decodeBase64URLJSON<T>(value: string, field: string): T {
  try {
    return JSON.parse(new TextDecoder().decode(base64URLToArrayBuffer(value))) as T;
  } catch {
    throw new Error(`invalid ${field}`);
  }
}

function parseAllowCredentials(
  value: string | null,
): PublicKeyCredentialDescriptorJSON[] | undefined {
  if (!value) {
    return undefined;
  }
  const credentials = decodeBase64URLJSON<unknown>(value, 'allow_credentials');
  if (!Array.isArray(credentials) || credentials.length === 0 || credentials.length > 20) {
    throw new Error('invalid allow_credentials');
  }
  return credentials.map((credential) => {
    if (
      !credential ||
      typeof credential !== 'object' ||
      (credential as PublicKeyCredentialDescriptorJSON).type !== 'public-key' ||
      typeof (credential as PublicKeyCredentialDescriptorJSON).id !== 'string'
    ) {
      throw new Error('invalid allow_credentials');
    }
    const { id } = credential as PublicKeyCredentialDescriptorJSON;
    if (base64URLToArrayBuffer(id).byteLength === 0) {
      throw new Error('invalid allow_credentials');
    }
    return { type: 'public-key', id };
  });
}

export function parseLocalSsoRequest(params: URLSearchParams): LocalSsoRequest {
  if (requiredParam(params, 'provider') !== LOCAL_SSO_PROVIDER) {
    throw new Error('unsupported identity provider');
  }

  const audience = new URL(requiredParam(params, 'aud'));
  const redirectURI = new URL(requiredParam(params, 'redirect_uri'));
  if (!isSecureOrigin(audience) || audience.origin !== redirectURI.origin) {
    throw new Error('invalid audience or redirect_uri');
  }
  if (redirectURI.pathname !== '/_clovyr/sso/callback' || redirectURI.search || redirectURI.hash) {
    throw new Error('invalid redirect_uri');
  }

  const clientID = requiredParam(params, 'client_id');
  if (clientID !== audience.origin) {
    throw new Error('client_id must match audience');
  }
  const challenge = requiredParam(params, 'challenge');
  if (base64URLToArrayBuffer(challenge).byteLength < 16) {
    throw new Error('invalid challenge');
  }
  const state = requiredParam(params, 'state');
  if (state.length < 16 || state.length > 1024) {
    throw new Error('invalid state');
  }
  const returnTo = params.get('return_to') || undefined;
  if (
    returnTo &&
    (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.length > 2048)
  ) {
    throw new Error('invalid return_to');
  }

  return {
    clientID,
    audience: audience.origin,
    redirectURI: redirectURI.toString(),
    challenge,
    state,
    returnTo,
    allowCredentials: parseAllowCredentials(params.get('allow_credentials')),
  };
}

export function localSsoCallbackPayload(
  request: LocalSsoRequest,
  assertion: PasskeyAuthenticationCredentialJSON,
): string {
  const payload: LocalSsoCallbackPayload = {
    version: 1,
    provider: LOCAL_SSO_PROVIDER,
    request,
    assertion,
  };
  return bytesToBase64URL(new TextEncoder().encode(JSON.stringify(payload)));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debugPasskey(message: string, ...details: any[]) {
  if (appConfig.VITE_APP_ENV === 'staging' || import.meta.env.DEV) {
    console.warn(`[Local SSO debug] ${message}`, ...details);
  }
}
